import { yupResolver } from "@hookform/resolvers/yup";
import { ArrowLeft, Mail, UserCircle } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import { useTranslation } from "react-i18next";

import { forgetPassword } from "core/Services/AuthServices";
import Button from "shared/Button";
import FormInput from "shared/FormInput";

interface ForgetPasswordFormData {
  email: string;
}

const ForgetPasswordForm = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { t } = useTranslation();

  const ForgetPasswordSchema = yup.object().shape({
    email: yup
      .string()
      .email(t("errors.validation.invalidEmail"))
      .required(t("errors.validation.emailRequired")),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgetPasswordFormData>({
    resolver: yupResolver(ForgetPasswordSchema),
  });

  const onSubmit = async (data: ForgetPasswordFormData) => {
    try {
      setIsSubmitting(true);
      await forgetPassword(data.email);
      toast.success(t("auth.passwordResetSuccess"));
      navigate("/");
    } catch (error: any) {
      toast.error(t("auth.passwordResetError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 flex items-center justify-center min-h-screen">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-8 space-y-6">
        <div className="flex justify-center mb-6">
          <UserCircle
            size={64}
            className="text-blue-500 opacity-80"
            strokeWidth={1.5}
          />
        </div>

        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-gray-800">
            {t("auth.forgotPassword")}
          </h2>
          <p className="text-gray-500 text-sm">{t("auth.enterEmail")}</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <FormInput
            label={t("common.email")}
            name="email"
            type="email"
            register={register}
            errors={errors}
            leftIcon={<Mail size={20} className="text-gray-400" />}
            placeholder={t("auth.enterEmail")}
          />

          <Button
            type="submit"
            onClick={handleSubmit(onSubmit)}
            fullWidth
            isLoading={isSubmitting}
            disabled={isSubmitting}
            icon={<Mail size={16} />}
            className="mt-4"
          >
            {t("auth.sendResetLink")}
          </Button>

          <div className="text-center mt-4">
            <Link
              to="/"
              className="text-gray-500 inline-flex items-center gap-2 
              hover:text-blue-600 transition-colors duration-300 text-sm"
            >
              <ArrowLeft size={16} /> {t("auth.backToLogin")}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgetPasswordForm;
